import { useLayoutEffect, useRef } from 'react';

import { useAppStore, type IAppStore } from '@/store';
import scrollElementToCenter from '@/utils/dom/scrollElementToCenter';

const selector = (store: IAppStore) => ({
  feedReturnSlug: store.feedReturnSlug,
  setFeedReturnSlug: store.setFeedReturnSlug,
});

/**
 * Seamless "return from the preset feed" scroll restore for a grid container.
 *
 * When the user closes the feed, the feed records the preset they left off at
 * in `feedReturnSlug`. This hook silently scrolls the container so the card
 * carrying `data-slug={slug}` is centered, then clears the flag. It runs before
 * paint (layout effect) with no animation, so the grid simply appears already
 * in position — the jump is never seen.
 *
 * Returns the ref to attach to the scrollable grid container.
 */
const useFeedReturnScroll = <T extends HTMLElement>() => {
  const containerRef = useRef<T>(null);
  const { feedReturnSlug, setFeedReturnSlug } = useAppStore(selector);

  useLayoutEffect(() => {
    if (!feedReturnSlug) return;

    const container = containerRef.current;
    const element = container?.querySelector<HTMLElement>(
      `[data-slug="${feedReturnSlug}"]`
    );
    if (container && element) {
      scrollElementToCenter({ container, element });
    }

    setFeedReturnSlug(null);
  }, [feedReturnSlug, setFeedReturnSlug]);

  return containerRef;
};

export default useFeedReturnScroll;
